import { fakerRU as faker } from '@faker-js/faker';
import {
  Device,
  DeviceType,
  type AddressContract,
  type Alert,
  type AlertCode,
  type DailySales,
  type Stats
} from 'types';
import { ALERTS_ICONS } from 'features/deviceAlerts';
import { DateTime } from 'luxon';
import { constant, curry, sumBy } from 'lodash';
import { signInMock } from './auth';
import { splitPercent } from './utils';

const getId = (() => {
  let id = 0;
  return () => {
    id += 1;
    return `00000000-0000-0000-0000-00000000000${id}`;
  };
})();

const SEGMENT_SIZE = 7;
const DAY_MINUTES = 1440;
const DAY_SEGMENTS = DAY_MINUTES / SEGMENT_SIZE;

function getDayValue<T extends number>(getValue: () => T) {
  const value = getValue();
  const currentSegment =
    DateTime.now().diff(DateTime.now().startOf('day'), 'minutes').minutes /
    SEGMENT_SIZE;
  const valuePercent = currentSegment / DAY_SEGMENTS;
  return Math.floor(value * valuePercent);
}

const salesTypeValue = curry((total: number, maxPercent: number) =>
  splitPercent(faker.number.int({ min: 0, max: maxPercent }), total)
);

interface CreateDeviceParams {
  type?: Device['Settings']['DeviceType'];
  SerialNumber?: string;
  maxLiters: number;
}

interface MockDevice extends Device {
  maxLiters: number;
  maxDayTotal: number;
}

function createDevices({
  type = DeviceType.Filter,
  SerialNumber = '',
  maxLiters
}: CreateDeviceParams): MockDevice {
  const Id = getId();
  const dailyLiters = getDayValue(constant(maxLiters));
  const price = type === DeviceType.Barrel ? 5 : 6;

  const total = dailyLiters * price;
  const deviceSales = salesTypeValue(total);
  const app = deviceSales(10);
  const banknotes = deviceSales(30);
  const bankCard = deviceSales(20);

  const osmosisPercent = faker.number.int({
    min: 80,
    max: 85
  });

  const osmosisPercentValue = splitPercent(osmosisPercent);
  const mineralPercentValue = splitPercent(100 - osmosisPercent);

  const totalSales = {
    Amount: total,
    Litres: dailyLiters,
    Banknotes: banknotes,
    Coins: total - app - banknotes - bankCard,
    Bankcard: bankCard,
    MobileApp: app,
    Exchange: 0
  };

  const sales = {
    Sum: totalSales,
    Sum_w0: {
      Amount: osmosisPercentValue(totalSales.Amount),
      Litres: osmosisPercentValue(totalSales.Litres),
      Banknotes: osmosisPercentValue(totalSales.Banknotes),
      Coins: osmosisPercentValue(totalSales.Coins),
      Bankcard: osmosisPercentValue(totalSales.Bankcard),
      MobileApp: osmosisPercentValue(totalSales.MobileApp),
      Exchange: 0
    },
    Sum_w1: {
      Amount: mineralPercentValue(totalSales.Amount),
      Litres: mineralPercentValue(totalSales.Litres),
      Banknotes: mineralPercentValue(totalSales.Banknotes),
      Coins: mineralPercentValue(totalSales.Coins),
      Bankcard: mineralPercentValue(totalSales.Bankcard),
      MobileApp: mineralPercentValue(totalSales.MobileApp),
      Exchange: 0
    }
  } satisfies DailySales;

  return {
    maxLiters,
    maxDayTotal: price * maxLiters,
    Id,
    DeviceState: {
      Alerts: [],
      SignalLevel: 63,
      LastOnline: +Date.now(),
      UpdatedAt: +Date.now(),
      Door: true,
      Lamp: true,
      HopMoney: true,
      HopStatus: '0300',
      BillValidorStatus: '0500',
      TubeMoney: faker.number.int({ min: 10, max: 100 }),
      High: null,
      Low: false,
      Leakage: false,
      Mineral: null,
      WasteWater: null,
      PouredOut: null,
      WaterInput: null,
      WaterOutput: 9.8,
      Banknotes: Math.max(
        sales.Sum.Amount,
        faker.number.int({ min: 1000, max: 10000 })
      ),
      Coins: Math.max(
        sales.Sum.Amount,
        faker.number.int({ min: 1000, max: 10000 })
      ),
      Link: 20,
      DiagDat: false,
      idCard: '0',
      HighLevel: true,
      TankMinLevel: false,
      TankEmpty: false,
      Tmp: 22,
      Csf: 0,
      Distance: 0,
      Tmp2: null,
      Tmp3: null,
      Bvp: 1,
      BalanceString: faker.number.int({ min: 10, max: 100 }).toString(),
      Balance: faker.number.int({ min: 10, max: 100 }),
      TurnOnAt: +Date.now(),
      Lat: 0,
      Lng: 0,
      VersionDt: +Date.now(),
      MnIn: 0,
      MnOut: faker.number.int({ min: 10, max: 20 }),
      Scp: faker.number.int({ min: 15, max: 30 }),
      Scc: faker.number.int({ min: 15, max: 30 }),
      Sci: 20.2,
      SciD: 0,
      Dpm: faker.number.float({ min: 4, max: 8, fractionDigits: 1 }),
      Dti: 27,
      Dtt: -128,
      Sfp: 0,
      Sfc: 0,
      Sce: 271,
      SceD: 0.9,
      Tfl: 1,
      Pfl: 4,
      Prf: null,
      Lnk: undefined,
      DailySales: sales,
      Fpc: undefined,
      Fpe: undefined,
      LastEncash: {
        Id: faker.string.uuid(),
        UserId: signInMock.User.Id,
        DeviceId: Id,
        Dts: +Date.now(),
        DeviceTimestamp: +Date.now(),
        Banknotes: faker.number.int({ min: 10000, max: 100000 }),
        Coins: faker.number.int({ min: 10000, max: 100000 })
      },
      LastSale: {
        Litres: faker.number.int({ min: 1, max: 19 }),
        DeviceId: Id,
        Dt: +Date.now(),
        DeviceTimestamp: +Date.now(),
        SId: 1,
        Banknotes: faker.number.int({ min: 100, max: 1000 }),
        Coins: faker.number.int({ min: 100, max: 1000 }),
        Exchange: faker.number.int({ min: 100, max: 1000 }),
        Volume: faker.number.int({ min: 100, max: 1000 }),
        Tag: [],
        Bankcard: faker.number.int({ min: 100, max: 1000 }),
        MobileApp: faker.number.int({ min: 100, max: 1000 }),
        Moneypured: faker.number.int({ min: 100, max: 1000 }),
        Amount: faker.number.int({ min: 100, max: 1000 })
      },
      Phone: '+70000000000',
      Version: 'v4.35.22'
    },
    Info: {
      CreatedAt: +Date.now(),
      Address: faker.location.streetAddress(),
      AddressContract: {
        PostalCode: faker.location.zipCode(),
        Country: faker.location.country(),
        Region: faker.location.state(),
        City: faker.location.city(),
        Street: faker.location.street(),
        House: faker.number.int({ min: 1, max: 10 }).toString(),
        Block: faker.number.int({ min: 1, max: 10 }).toString(),
        Flat: faker.number.int({ min: 1, max: 10 }).toString()
      } satisfies AddressContract,
      CostPerLiter: price,
      CostPerLiterByCard: price + 2,
      CostPerLiterMineral: 10,
      Currency: 'RUB',
      DeviceKey: Id,
      Timezone: 'Europe/Moscow',
      LastService: +Date.now(),
      Phone: '+70000000000',
      PosLat: null,
      PosLng: null,
      SerialNumber,
      SupportPhone: '+71000000000',
      UserId: signInMock.User.Id,
      Group: 'ru',
      AllowMineralPayment: true,
      IsActivated: true
    },
    Settings: {
      AllowBottling: true,
      Prepare: {
        Imp: 1200,
        Imc: 1200,
        Imi: 10,
        Ime: 3200,
        Tfl: 1,
        Pfl: 4
      },
      Gprs: {
        BalanceRegex: null,
        MobileOperator: 'megafon',
        UssdRequestBalance: null,
        UssdRequestNumber: null
      },
      Main: {
        Kup: 10,
        Mon: 1,
        Sco: 335,
        Prs: 5,
        Prc: 5,
        Prb: 5,
        Val: 0,
        Dgd: 6,
        Otp: 3,
        Idc: 0,
        Nfc: 1,
        Tmm: 10,
        Tmx: 15,
        Dsp: 2,
        Ast: 0,
        Sbb: 180,
        Fex: 2,
        Ozn: 10,
        Uzd: 0,
        V98: 2,
        Asi: 5,
        Bcs: 0,
        Dzn: 0
      },
      Notifications: {
        BalanceLimit: 0,
        EnableDoor: false,
        EnableHigh: false,
        EnableLamp: false,
        EnableLeakage: false,
        EnableLow: false,
        EnableTankEmpty: true,
        EnableTankFull: false,
        SendEmailDailyReport: false,
        EnableLowTemperatureSensor: true
      },
      DeviceType: type,
      BarrelMax: 2000,
      BarrelVolumeCorrect: 0,
      FiscalService: 0,
      FiscalKkm: null,
      FiscalLogin: null,
      FiscalPassword: null,
      EnableMoneyAcceptors: true,
      RestartMoneyAcceptors: false,
      ReloadSettings: false,
      ResetWaterOutput: false,
      EnableWaterprepare: true,
      BacklightOption: 0,
      SunCalcDate: '2025-08-21',
      SunCalcSunrise: '05:33',
      SunCalcSunset: '19:42'
    },
    Features: {
      HasTempSensor1: true,
      HasTempSensor2: true,
      HasTempSensor3: true,
      IsTuneMainboard: true,
      IsTuneTempSensor2: true,
      IsControlV3: true,
      HasWaterprepare: true,
      IsWaterprepareSettingsV2: true,
      IsDeviceOnlineVersion: true,
      IsWifiOnboard: true
    },
    Cartridges: {
      PP5: {
        Rr: 20,
        Mr: 20,
        BackendDebug: {
          Start: 20.2,
          DtStart: new Date().toISOString(),
          MaxRes: 20
        }
      },
      GAC: {
        Rr: 20,
        Mr: 20,
        BackendDebug: {
          Start: 20.2,
          DtStart: new Date().toISOString(),
          MaxRes: 20
        }
      },
      PP1: {
        Rr: 20,
        Mr: 20,
        BackendDebug: {
          Start: 20.2,
          DtStart: new Date().toISOString(),
          MaxRes: 20
        }
      },
      RO: {
        Rr: 193.1,
        Mr: 100,
        BackendDebug: {
          Start: 102.9,
          DtStart: new Date().toISOString(),
          MaxRes: 100
        }
      },
      Mineral: {
        Rr: 48.9,
        Mr: 50,
        BackendDebug: {
          Start: 8.7,
          DtStart: new Date().toISOString(),
          MaxRes: 50
        }
      },
      'GAC-PP': {
        Rr: 143.1,
        Mr: 50,
        BackendDebug: {
          Start: 102.9,
          DtStart: new Date().toISOString(),
          MaxRes: 50
        }
      },
      UV: {
        Rr: 58,
        Mr: 358,
        BackendDebug: {
          DtStart: new Date().toISOString(),
          MaxRes: 358
        }
      }
    },
    Username: signInMock.User.Username
  };
}

export const devices = [
  createDevices({
    maxLiters: 500
  }),
  createDevices({
    maxLiters: 800
  }),
  createDevices({
    maxLiters: 1000
  }),
  createDevices({
    maxLiters: 1200
  }),
  createDevices({
    maxLiters: 1800
  }),
  createDevices({
    type: DeviceType.Barrel,
    SerialNumber: 'Привозная вода',
    maxLiters: 1500
  })
];

const alertDevice = faker.helpers.arrayElement(devices);
alertDevice.DeviceState.Alerts = faker.helpers.uniqueArray(
  Object.keys(ALERTS_ICONS).map(
    (Code) =>
      ({
        Code: Code as AlertCode,
        Desc: Code
      }) satisfies Alert
  ),
  faker.number.int({ min: 1, max: 3 })
);

alertDevice.Info.SerialNumber =
  alertDevice.Info.SerialNumber ?? 'Аппарат с предупреждениями';

export const happyhour = {
  priceInfo: {
    waterPrice: 5,
    deviceTz: 3
  },
  mon: {
    fromHour: 15,
    toHour: 17,
    waterDiscountPrice: 5,
    isEnabled: false
  },
  tue: {
    fromHour: 15,
    toHour: 17,
    waterDiscountPrice: 5,
    isEnabled: false
  },
  wed: {
    fromHour: 15,
    toHour: 17,
    waterDiscountPrice: 5,
    isEnabled: false
  },
  thu: {
    fromHour: 15,
    toHour: 17,
    waterDiscountPrice: 5,
    isEnabled: false
  },
  fri: {
    fromHour: 15,
    toHour: 17,
    waterDiscountPrice: 5,
    isEnabled: false
  },
  sat: {
    fromHour: 15,
    toHour: 17,
    waterDiscountPrice: 5,
    isEnabled: false
  },
  sun: {
    fromHour: 15,
    toHour: 17,
    waterDiscountPrice: 5,
    isEnabled: false
  }
};

type MockStats = Omit<
  Stats,
  'MineralLitres' | 'Cash' | 'Sales' | 'Osmosis' | 'MineralAmount'
>;

const deviceStats = Object.keys({
  Litres: 0,
  Amount: 0,
  Banknotes: 0,
  Coins: 0,
  Bankcard: 0,
  MobileApp: 0,
  Exchange: 0
} satisfies MockStats).reduce(
  (acc, key) => {
    const fieldName = key as keyof MockStats;
    acc[fieldName] = splitPercent(
      faker.number.int({ min: 75, max: 100 }),
      sumBy(
        devices,
        (device) => device.DeviceState.DailySales.Sum[fieldName] ?? 0
      )
    );
    return acc;
  },
  {} as Record<keyof MockStats, number>
);

export const stats = [
  '1757624400000',
  '1757710800000',
  '1757797200000',
  '1757883600000',
  '1757970000000',
  '1758056400000',
  '1758142800000',
  '1758229200000'
].reduce(
  (acc, date) => {
    const calculateMock = (value: number) =>
      splitPercent(faker.number.int({ min: 75, max: 100 }), value);

    const Litres = calculateMock(deviceStats.Litres);
    const MineralLitres = splitPercent(
      faker.number.int({ min: 15, max: 20 }),
      deviceStats.Litres
    );
    acc[date] = {
      Banknotes: calculateMock(deviceStats.Banknotes),
      Coins: calculateMock(deviceStats.Coins),
      Cash: calculateMock(deviceStats.Banknotes + deviceStats.Coins),
      Bankcard: calculateMock(deviceStats.Bankcard),
      MobileApp: calculateMock(deviceStats.MobileApp),
      Exchange: calculateMock(deviceStats.Exchange),
      Amount: calculateMock(deviceStats.Amount),
      Litres,
      Sales: faker.number.int({ min: 100, max: 1000 }),
      MineralLitres,
      Osmosis: Litres - MineralLitres,
      MineralAmount: splitPercent(
        faker.number.int({ min: 15, max: 20 }),
        deviceStats.Amount
      )
    } satisfies Stats;
    return acc;
  },
  {} as Record<string, Stats>
);

export const telemetry = {
  Items: [
    {
      Dt: 1757693267612,
      DtD: 18450410,
      Dpm: null,
      Dti: 25,
      Dtt: -128,
      MnIn: 0,
      MnOut: 13,
      Scc: 0,
      Sce: 0.464,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1757758411469,
      DtD: 65143857,
      Dpm: null,
      Dti: 26,
      Dtt: -128,
      MnIn: 0,
      MnOut: 18,
      Scc: 0,
      Sce: 1.563,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1757771904581,
      DtD: 13493112,
      Dpm: null,
      Dti: 26,
      Dtt: -128,
      MnIn: 0,
      MnOut: 8,
      Scc: 0,
      Sce: 0.302,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1757774629972,
      DtD: 2725391,
      Dpm: null,
      Dti: 25,
      Dtt: -128,
      MnIn: 0,
      MnOut: 12,
      Scc: 0,
      Sce: 0.086,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1757828753527,
      DtD: 54123555,
      Dpm: null,
      Dti: 23,
      Dtt: -128,
      MnIn: 0,
      MnOut: 2,
      Scc: 0,
      Sce: 1.253,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1757853278943,
      DtD: 24525416,
      Dpm: null,
      Dti: 29,
      Dtt: -128,
      MnIn: 0,
      MnOut: 13,
      Scc: 0,
      Sce: 0.708,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1758040445548,
      DtD: 187166605,
      Dpm: null,
      Dti: 26,
      Dtt: -128,
      MnIn: 0,
      MnOut: 12,
      Scc: 0,
      Sce: 4.849,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1758103638122,
      DtD: 63192574,
      Dpm: null,
      Dti: 27,
      Dtt: -128,
      MnIn: 0,
      MnOut: 13,
      Scc: 0,
      Sce: 1.591,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1758201297335,
      DtD: 97659213,
      Dpm: null,
      Dti: 24,
      Dtt: -128,
      MnIn: 0,
      MnOut: 13,
      Scc: 0,
      Sce: 2.489,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    },
    {
      Dt: 1758222547172,
      DtD: 21249837,
      Dpm: null,
      Dti: 22,
      Dtt: -128,
      MnIn: 0,
      MnOut: 3,
      Scc: 0,
      Sce: 0.601,
      Sci: 0,
      Scp: 0,
      Sfp: 0,
      Sfc: 0
    }
  ],
  Count: 11
};
